import React, { memo, useState, useEffect } from 'react';
import cn from 'classnames';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import Icon from '../../../../Icon';
import Button from '../../../../Button';
import Checkbox from '../../../../common/Checkbox';
import DocumentsSection from '../DocumentsSection';
import PlainTextErrorBlock from '../../../../common/ErrorBlock/PlainTextErrorBlock';
import AdditionalInsuranceCollapse from '../AdditionalInsuranceCollapse';
import DeleteAdditionalInsuranceConfirmationModal from '../DeleteAdditionalInsuranceConfirmationModal';
import MainInsuranceFormSection from '../../../../ProfileLayout/InsuranceDetails/MainInsuranceFormSection';
import PrimarySubscriberFormSection from '../../../../ProfileLayout/InsuranceDetails/PrimarySubscriberFormSection';

import { SHOW_ADDITIONAL_STYLES_FOR_ADDITIONAL_INSURANCES } from '../../../../../constants/insurance-flow-type';
import { INSURANCE_ELIGIBILITY_FIELDS_TYPES } from '../../../../../constants/formFields';

import { getAdditionalInsuranceCardDocumentsConfig } from '../../../../../utils/getDocumentsConfig';

import useInsuranceRelationOptions from '../../../../../hooks/selectOptions/useInsuranceRelationOptions';
import useIsAdditionalInsurancesEnabledAndMaxNumber from '../../../../../hooks/patientInsurance/useIsAdditionalInsurancesEnabledAndMaxNumber';

import { manageInsurancesActions } from '../../../../../redux/manage-insurances/actions/manage-insurances-actions';
import { actions as deletePatientInsurance } from '../../../../../redux/manage-insurances/requests/delete-patient-insurance';
import { actions as getPatientInsurancesListActions } from '../../../../../redux/manage-insurances/requests/get-patient-insurances-list';

import { selectIsInsuranceDeleteSucceeded } from '../../../../../redux/manage-insurances/selectors/delete-patient-insurance';
import { checkIsNeedToShowErrorMessage } from './utils/check-is-need-to-show-error-message';

import styles from './index.styl';

const { PRIMARY_SUBSCRIBER } = INSURANCE_ELIGIBILITY_FIELDS_TYPES;

const AdditionalInsurancesSection = ({
  addAdditionalInsurance,
  additionalInsurances,
  deleteAdditionalInsurance,
  entities,
  flowType,
  frozenAdditionalInsurancesListIds,
  handleAdditionalInsuranceChangeField,
  handleAdditionalInsuranceFormChange,
  handleAdditionalInsuranceHealthPlanChange,
  handleAdditionalInsurancePrimarySubscriberChange,
  handleInputChange,
  handleMenuScrollToBottom,
  isLoading,
  primarySubscriberCheckboxLabel,
  taskAssignmentId,
  userId,
}) => {
  const [opened, setOpened] = useState(false);

  const [
    confirmDeleteInsuranceIndex,
    setConfirmDeleteInsuranceIndex,
  ] = useState(undefined);

  const [patientInsuranceId, setPatientInsuranceId] = useState(undefined);

  const [expandedSections, setExpandedSections] = useState([]);

  const dispatch = useDispatch();

  const { formatMessage } = useIntl();

  const {
    maxAdditionalInsurancesNumber,
  } = useIsAdditionalInsurancesEnabledAndMaxNumber();

  const { hideInsuranceRelationFieldOrInfo } = useInsuranceRelationOptions();

  const isInsuranceDeleteSucceeded = useSelector(
    selectIsInsuranceDeleteSucceeded,
  );

  const withAdditionalStyles = SHOW_ADDITIONAL_STYLES_FOR_ADDITIONAL_INSURANCES.includes(
    flowType,
  );

  const showAddAdditionalInsuranceButton =
    additionalInsurances.length < maxAdditionalInsurancesNumber;

  const additionalInsuranceTitle = formatMessage({
    id: 'profile.insurance.additional',
  });

  const addInsuranceTitle = formatMessage({ id: 'common.addInsurance' });

  const deleteInsuranceTitle = formatMessage({ id: 'common.deleteInsurance' });

  const additionalInsuranceFormDetailsError = formatMessage({
    id: 'errors.provideAdditionalInsuranceDetails',
  });

  const closeDeleteAdditionalInsuranceModal = () => {
    setOpened(false);
    setConfirmDeleteInsuranceIndex(undefined);
    setPatientInsuranceId(undefined);
  };

  const handleSectionToggle = index => () => {
    const newExpandedSections = expandedSections.map((section, i) => {
      if (i === index) {
        return { ...section, isOpened: !section.isOpened };
      }

      return section;
    });

    setExpandedSections(newExpandedSections);
  };

  const handleDeleteAdditionalInsuranceFromFormState = index => {
    deleteAdditionalInsurance(index)();

    setExpandedSections(prev => {
      const newExpandedSections = [...prev];
      newExpandedSections.splice(index, 1);
      return newExpandedSections;
    });
  };

  const toggleConfirmation = (index, insuranceId) => () => {
    if (!insuranceId) {
      handleDeleteAdditionalInsuranceFromFormState(index);
    } else {
      setOpened(prev => !prev);
      setConfirmDeleteInsuranceIndex(index);
      setPatientInsuranceId(insuranceId);
    }
  };

  const onAddAdditionalInsuranceButtonClick = () => {
    addAdditionalInsurance();
    setExpandedSections(prev => [...prev, { isOpened: true }]);
  };

  const removeInsuranceFromList = () => {
    handleDeleteAdditionalInsuranceFromFormState(confirmDeleteInsuranceIndex);

    closeDeleteAdditionalInsuranceModal();
  };

  const handleDeleteAdditionalInsurance = () => {
    if (patientInsuranceId) {
      dispatch(deletePatientInsurance.send({ userId, patientInsuranceId }));
    } else {
      removeInsuranceFromList();
    }
  };

  const handleOnImageChange = (index, insuranceId) => (
    fieldName,
    imgValue,
    imgId,
  ) => {
    dispatch(
      manageInsurancesActions.uploadInsuranceCard({
        fieldName,
        flowType,
        imgId,
        imgValue,
        patientInsuranceId: insuranceId,
        userId,
        handleAdditionalInsuranceFormChange: handleAdditionalInsuranceFormChange(
          index,
        ),
        ...(taskAssignmentId && { taskAssignmentId }),
      }),
    );
  };

  useEffect(() => {
    if (isInsuranceDeleteSucceeded) {
      removeInsuranceFromList();
      dispatch(
        getPatientInsurancesListActions.removePatientInsuranceFromList({
          id: patientInsuranceId,
        }),
      );
      dispatch(deletePatientInsurance.clear());
    }
  }, [isInsuranceDeleteSucceeded]);

  useEffect(() => {
    const additionalInsurancesAreExisting = get(
      additionalInsurances,
      'length',
      0,
    );

    if (additionalInsurancesAreExisting) {
      const initialState = additionalInsurances.map(() => ({
        isOpened: false,
      }));
      setExpandedSections(initialState);
    }
  }, []);

  return (
    <div className={styles.wrapper}>
      <section className={styles.additionalInsurance}>
        <div className={styles.additionalInsuranceTitle}>
          {additionalInsuranceTitle}
        </div>
      </section>

      {additionalInsurances.map((insurance, index) => {
        const { formState, formErrors } = insurance || {};

        const isAdditionalInsurancePrimarySubscriber = Boolean(
          formState[PRIMARY_SUBSCRIBER],
        );

        const insuranceId = get(formState, 'id');

        const fieldName = `additionalInsurance-${index}-documents`;

        const healthPlan = get(formState, 'healthPlan', null);
        const isPlanNotListedSelected = Boolean(healthPlan && !healthPlan.id);

        const documents = [
          {
            key: `${fieldName}_1`,
            attachment: get(formState, 'frontSide'),
          },

          {
            key: `${fieldName}_2`,
            attachment: get(formState, 'backSide'),
          },
        ];

        const insuranceDocumentsConfig = getAdditionalInsuranceCardDocumentsConfig(
          {
            formatMessage,
            userDocuments: documents,
            isEditMode: true,
            fieldName,
          },
        );

        const isSectionOpened =
          get(expandedSections[index], 'isOpened') || false;

        const showError = checkIsNeedToShowErrorMessage({
          flowType,
          insuranceData: formState,
          hideInsuranceRelationFieldOrInfo,
          frozenAdditionalInsurancesListIds,
        });

        return (
          <AdditionalInsuranceCollapse
            flowType={flowType}
            key={`additional-insurance-${index}`}
            opened={isSectionOpened}
            onClick={handleSectionToggle(index)}
            showError={showError}
          >
            <div
              className={cn(styles.additionalInsuranceFormWrapper, {
                [styles.additionalInsuranceFormWrapper_additionalStyles]: withAdditionalStyles,
              })}
            >
              {showError && (
                <PlainTextErrorBlock
                  errors={additionalInsuranceFormDetailsError}
                  className={styles.errorBlock}
                />
              )}

              <DocumentsSection
                config={insuranceDocumentsConfig}
                documents={documents}
                onImageChange={handleOnImageChange(index, insuranceId)}
              />

              <MainInsuranceFormSection
                formState={formState}
                handleHealthPlanChange={handleAdditionalInsuranceHealthPlanChange(
                  index,
                )}
                isPlanNotListedSelected={isPlanNotListedSelected}
                entities={entities}
                handleInputChange={handleInputChange}
                handleMenuScrollToBottom={handleMenuScrollToBottom}
                isLoading={isLoading}
                handleFormChange={handleAdditionalInsuranceFormChange(index)}
              />

              <div className={styles.primarySubscriberCheckbox}>
                <Checkbox
                  size={16}
                  label={primarySubscriberCheckboxLabel}
                  disabled={isPlanNotListedSelected}
                  id={`additionalInsurance-${index}-${PRIMARY_SUBSCRIBER}`}
                  checked={isAdditionalInsurancePrimarySubscriber}
                  onChange={handleAdditionalInsurancePrimarySubscriberChange(
                    index,
                  )}
                />
              </div>

              {!isAdditionalInsurancePrimarySubscriber && (
                <PrimarySubscriberFormSection
                  formErrors={formErrors}
                  formState={formState}
                  handleChangeField={handleAdditionalInsuranceChangeField(
                    index,
                  )}
                  handleFormChange={handleAdditionalInsuranceFormChange(index)}
                />
              )}

              <div
                className={cn(styles.deleteInsuranceButtonWrapper, {
                  [styles.deleteInsuranceButtonWrapper_additionalStyles]: withAdditionalStyles,
                })}
              >
                <Button
                  type="button"
                  raised
                  onClick={toggleConfirmation(index, insuranceId)}
                  color="accent"
                  className={styles.deleteInsuranceButton}
                >
                  {deleteInsuranceTitle}
                </Button>
              </div>
            </div>
          </AdditionalInsuranceCollapse>
        );
      })}

      {showAddAdditionalInsuranceButton && (
        <Button
          type="button"
          raised
          className={styles.addInsuranceButton}
          onClick={onAddAdditionalInsuranceButtonClick}
          color="accent"
        >
          <Icon name="plus" size={16} />
          {addInsuranceTitle}
        </Button>
      )}

      {opened && (
        <DeleteAdditionalInsuranceConfirmationModal
          onClose={closeDeleteAdditionalInsuranceModal}
          onDelete={handleDeleteAdditionalInsurance}
        />
      )}
    </div>
  );
};

AdditionalInsurancesSection.propTypes = {
  addAdditionalInsurance: PropTypes.func,
  additionalInsurances: PropTypes.array,
  deleteAdditionalInsurance: PropTypes.func,
  entities: PropTypes.array,
  flowType: PropTypes.string,
  frozenAdditionalInsurancesListIds: PropTypes.array,
  handleAdditionalInsuranceChangeField: PropTypes.func,
  handleAdditionalInsuranceFormChange: PropTypes.func,
  handleAdditionalInsuranceHealthPlanChange: PropTypes.func,
  handleAdditionalInsurancePrimarySubscriberChange: PropTypes.func,
  handleInputChange: PropTypes.func,
  handleMenuScrollToBottom: PropTypes.func,
  isLoading: PropTypes.bool,
  primarySubscriberCheckboxLabel: PropTypes.string,
  taskAssignmentId: PropTypes.number,
  userId: PropTypes.number,
};

export default memo(AdditionalInsurancesSection);



// WEBPACK FOOTER //
// ./src/components/forms/InsuranceForm/components/AdditionalInsurancesSection/index.jsx